import { cn } from '@shadcn/lib/utils';
import { Card, CardContent } from '@shadcn/ui/card';
import { cva, type VariantProps } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../button/button';
import { Text } from '../text/text';

const bannerVariants = cva('p-3 border shadow-none rounded-xl flex items-center', {
  variants: {
    variant: {
      info: 'bg-dodger-100 border-dodger-200',
      success: 'bg-status-green-light border-success',
      warning: 'bg-status-amber-light border-warning',
      error: 'bg-blush-100 border-error',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

type BannerVariant = VariantProps<typeof bannerVariants>['variant'];

function Banner({ className, variant, initialIsShown = true, onDismiss, children, ...props }: IBannerProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState<boolean>(initialIsShown);
  const [closing, setClosing] = useState<boolean>(false);

  if (!visible) return null;

  const handleDismiss = () => {
    setClosing(true);
    onDismiss?.();
  };

  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-200 ease-out',
        closing ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
      )}
      onTransitionEnd={(event) => {
        if (closing && event.currentTarget === event.target) {
          setVisible(false);
        }
      }}
    >
      <div className="min-h-0 overflow-hidden">
        <Card
          className={cn('p-0 shadow-none transition-opacity duration-200', closing && 'opacity-0', className)}
          {...props}
        >
          <CardContent className={cn(bannerVariants({ variant }))}>
            <Text className={cn('flex-1 leading-snug')} size="sm">
              {children}
            </Text>
            <Button
              aria-label={t('ui-components.banner.dismiss')}
              className="bg-transparent hover:bg-transparent p-0 has-[>svg]:px-0"
              onClick={handleDismiss}
            >
              <XIcon aria-hidden="true" className="size-5 text-black" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface IBannerProps extends React.ComponentPropsWithoutRef<'div'> {
  readonly variant?: BannerVariant;
  readonly initialIsShown?: boolean;
  readonly onDismiss?: () => void;
  readonly children: React.ReactNode;
}

export { Banner, type BannerVariant, type IBannerProps };
